import { ToolCommentIcon } from 'components/Tools';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import styled from 'styled-components';
import bfbTheme from 'styles/b4b-theme';

const MODULES = {
  toolbar: {
    container: [
      ['bold', 'italic', 'underline', 'strike'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      // BUG: Backend is failing with 403 when the text contains links
      // ['link'],
    ],
  },
};

function TextEditor({
  className = '',
  highlightComments = false,
  initialHTML = '',
  onBlur = null,
  showComments = false,
  placeholder = '',
  editorStyle = {},
  handleCommentClick,
  id,
  max = undefined,
  onInputLengthChange = () => {},
}) {
  const quillRef = useRef(null);
  const [charCount, setCharCount] = useState(0);
  const myCommentIconId = `text-editor-component-comment-icon-${id}`;

  const isMaxReached = max && charCount > max;

  const handleOnChange = (_content, _delta, _source, editor) => {
    const text = editor.getText();
    // We subtract 1 because an empty Quill editor inherently contains a newline character (\n)
    // as per their documentation
    const textLength = text.length - 1;

    setCharCount(textLength);

    // Update external state to show checklist
    onInputLengthChange({
      length: textLength,
      lineBreaks: (text.match(/\n/g) || []).length,
    });
  };

  const handleOnBlur = (_previousRange, source, editor) => {
    // PATCH: Avoid firing onBlur with empty string on Paste (Ctrl+V)
    if (source !== 'user') return;

    if (!onBlur) return;

    onBlur(editor.getHTML());
  };

  // Update char count on first render with the initial value
  useEffect(() => {
    if (quillRef.current) {
      // We subtract 1 because an empty Quill editor inherently contains a newline character (\n)
      // as per their documentation
      const textLength = quillRef.current.getEditor().getText().length - 1;
      setCharCount(textLength);
    }
  }, [initialHTML]);

  // Convert Pasted Copy from Clipboard into plain text.
  useEffect(() => {
    const editor = quillRef.current.getEditor();
    const editorNode = editor.root;

    const handlePaste = e => {
      // Stop Quill's default paste handling
      e.preventDefault();

      // Get plain text from clipboard
      const plainText = e.clipboardData.getData('text/plain');

      // Use Quill's built-in insertText at current selection
      const range = editor.getSelection(true); // true = focus cursor
      if (range) {
        editor.deleteText(range.index, range.length); // remove selected text if any
        editor.insertText(range.index, plainText, 'user'); // insert plain text
        setTimeout(() => {
          // PATCH: doesn't work otherwise
          editor.setSelection(range.index + plainText.length, 0); // move cursor to end
        }, 300);
      } else {
        // fallback: append at the end
        editor.insertText(editor.getLength(), plainText, 'user');
      }
    };

    editorNode.addEventListener('paste', handlePaste);

    return () => editorNode.removeEventListener('paste', handlePaste);
  }, []);

  return (
    <StyledTextEditor
      className={className}
      $isMaxReached={isMaxReached}
      $minHeight={editorStyle.minHeight}
      $maxHeight={editorStyle.maxHeight}
    >
      <div className="editor-container">
        <ReactQuill
          ref={quillRef}
          style={editorStyle}
          defaultValue={initialHTML}
          onChange={handleOnChange}
          onBlur={handleOnBlur}
          placeholder={placeholder}
          modules={MODULES}
        />
        {max && <span className="char-counter">{`${charCount}/${max}`}</span>}
      </div>

      {showComments && (
        <aside>
          <i
            style={{
              paddingLeft: '10px',
            }}
            id={myCommentIconId}
          >
            <ToolCommentIcon active={highlightComments} onClick={handleCommentClick} />
          </i>
        </aside>
      )}
    </StyledTextEditor>
  );
}

const StyledTextEditor = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;

  .editor-container {
    flex: 1;
    min-width: calc(100% - 40px);
  }

  /* Targeting Quill's main container */
  .ql-container {
    border: 1px solid ${({ $isMaxReached }) => ($isMaxReached ? bfbTheme.error : '#E0E0E0')};
    border-radius: 4px;
    border-top: none; /* The toolbar has the top border */
  }

  .ql-toolbar {
    border: 1px solid ${({ $isMaxReached }) => ($isMaxReached ? bfbTheme.error : '#E0E0E0')};
    border-bottom: 1px solid #e0e0e0;
    border-radius: 4px 4px 0 0;
  }

  .ql-editor {
    overflow: auto;
    padding: 6px 8px;
    min-height: ${({ $minHeight }) => `${$minHeight}px` || '100px'};
    max-height: ${({ $maxHeight }) => `${$maxHeight}px` || 'none'};
  }

  .char-counter {
    float: right;
    font-size: 14px;
    margin-top: 4px;
    color: ${({ $isMaxReached }) => ($isMaxReached ? bfbTheme.error : bfbTheme.grey)};
  }
`;

TextEditor.propTypes = {
  className: PropTypes.string,
  editorStyle: PropTypes.instanceOf(Object),
  handleCommentClick: PropTypes.func.isRequired,
  highlightComments: PropTypes.bool,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  initialHTML: PropTypes.string,
  max: PropTypes.number,
  onBlur: PropTypes.func,
  showComments: PropTypes.bool,
  placeholder: PropTypes.string,
  onInputLengthChange: PropTypes.func,
};

export default TextEditor;
